import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { api } from "../lib/api";
import { hasPlanFeature, planName } from "../lib/plans";

const AuthContext = createContext(null);

function applyTheme(theme) {
  const dark = String(theme || "CLARO").toUpperCase() === "OSCURO";
  document.documentElement.classList.toggle("dark", dark);
  document.documentElement.style.colorScheme = dark ? "dark" : "light";
}

export function AuthProvider({ children }) {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const refreshSession = async () => {
    try {
      const response = await api.get("/api/auth/me");
      setUser(response.data?.user || null);
      applyTheme(response.data?.user?.tema);
      return response.data?.user || null;
    } catch {
      setUser(null);
      applyTheme("CLARO");
      return null;
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refreshSession();
  }, []);

  useEffect(() => {
    const handleInvalidSession = () => {
      setUser(null);
      applyTheme("CLARO");
    };
    window.addEventListener("nexoprint:session-invalid", handleInvalidSession);
    return () => window.removeEventListener("nexoprint:session-invalid", handleInvalidSession);
  }, []);

  const login = async (credentials) => {
    const response = await api.post("/api/auth/login", credentials);
    const nextUser = response.data?.user || null;
    setUser(nextUser);
    applyTheme(nextUser?.tema);
    return nextUser;
  };

  const logout = async () => {
    await api.post("/api/auth/logout").catch(() => undefined);
    setUser(null);
    applyTheme("CLARO");
  };

  const updateUser = (nextUser) => {
    setUser(nextUser);
    applyTheme(nextUser?.tema);
  };

  const value = useMemo(() => ({ user, loading, login, logout, updateUser, refreshSession }), [user, loading]);
  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth debe utilizarse dentro de AuthProvider");
  return context;
}

export function RequireAuth({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-100 text-sm font-semibold text-slate-500 dark:bg-slate-950 dark:text-slate-300">
        Validando sesion...
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace state={{ from: location.pathname + location.search }} />;
  return children;
}

export function RequirePlanFeature({ feature, children }) {
  const { user, loading } = useAuth();
  if (loading) return null;
  if (hasPlanFeature(user, feature)) return children;
  return (
    <div className="mx-auto mt-12 max-w-2xl border border-amber-200 bg-white p-8 text-center shadow-sm dark:border-amber-800 dark:bg-slate-900">
      <h1 className="text-2xl font-black text-slate-950 dark:text-white">Funcion no incluida en tu plan</h1>
      <p className="mt-3 text-slate-600 dark:text-slate-300">Esta seccion no esta disponible en el plan {planName(user)}.</p>
      <a href="/soporte" className="mt-5 inline-flex rounded-md bg-cyan-700 px-4 py-2 font-extrabold text-white hover:bg-cyan-800">Consultar al servicio tecnico</a>
    </div>
  );
}
